import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CalendarViewMode } from '@/components/CalendarView';

/**
 * Slice 13c — shared calendar helpers used by both CalendarPage
 * (project-scoped) and WorkspaceHomePage (workspace-wide). Lives
 * outside CalendarView itself so the page-side state shape stays
 * separate from the rendering component.
 */

const fmt = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const startOfWeekMonday = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  // Mon=1, Sun=0; we want Mon as week start.
  const dow = x.getDay();
  const diff = (dow === 0 ? 6 : dow - 1);
  x.setDate(x.getDate() - diff);
  return x;
};

/**
 * Compute the inclusive `from` / `to` date strings the calendar
 * needs to fetch given the current view + focal day. The range is
 * the visible window plus a small buffer so adjacent navigation
 * (one day forward, one week forward) usually hits the cache.
 *
 *  - Day view   → focal ± 3 days   (7-day window)
 *  - Week view  → focal-week ± 1 week (~3 weeks)
 *  - Month view → focal-month-grid ± 1 week (covers the 6-row grid)
 *
 * Cache key is the from/to pair, so navigating into a fresh window
 * fires a re-fetch with narrow params; staying within window is a
 * cache hit (no network).
 */
export const computeEventsRangeForView = (
  view: CalendarViewMode,
  focal: Date,
): { from: string; to: string } => {
  if (view === 'day') {
    const from = new Date(focal);
    from.setDate(from.getDate() - 3);
    const to = new Date(focal);
    to.setDate(to.getDate() + 3);
    return { from: fmt(from), to: fmt(to) };
  }
  if (view === 'week') {
    const start = startOfWeekMonday(focal);
    const from = new Date(start);
    from.setDate(from.getDate() - 7);
    const to = new Date(start);
    to.setDate(to.getDate() + 13);
    return { from: fmt(from), to: fmt(to) };
  }
  // month
  const monthStart = new Date(focal.getFullYear(), focal.getMonth(), 1);
  const monthGridStart = startOfWeekMonday(monthStart);
  const from = new Date(monthGridStart);
  from.setDate(from.getDate() - 7);
  const to = new Date(monthGridStart);
  to.setDate(to.getDate() + 49); // 6 rows × 7 days + 1 week buffer
  return { from: fmt(from), to: fmt(to) };
};

/**
 * Brand-accent "+ New event" CTA. Mirrors `NewTaskCtaButton`'s
 * shape but uses a translucent brand-accent fill so the two CTAs
 * sit next to each other in the topbar without visual conflict —
 * tasks read as "primary action" (solid navy), events as "secondary
 * action of the same family" (orange-tinted).
 */
export const NewEventCtaButton = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-[12.5px] font-medium transition-shadow duration-100',
      'border-[var(--brand-accent)]/40 bg-[oklch(0.74_0.16_50/0.10)] text-foreground',
      'hover:bg-[oklch(0.74_0.16_50/0.18)] hover:border-[var(--brand-accent)]/60',
      'dark:bg-[oklch(0.74_0.16_50/0.14)]',
    )}
  >
    <Plus className="size-3.5" aria-hidden="true" />
    New event
  </button>
);
